/**
 * StreamingFilePicker
 *
 * A pure, controlled file picker component with no Formik dependency.
 * Shows upload progress and status, supports retry on failure.
 * UI matches the existing InputFileWithViewSingle component.
 *
 * Supports both single-file and multi-file modes:
 *
 * Single-file usage (default):
 * <StreamingFilePicker
 *   accept=".xlsx"
 *   fileTypeHint="*XLSX File (Not more than 10 MB)"
 *   fileState={upload.filesByKey['templateXlsx']}
 *   onFileChange={(file) => upload.setFile('templateXlsx', file)}
 *   onRetry={() => upload.retryFile('templateXlsx')}
 * />
 *
 * Multi-file usage:
 * <StreamingFilePicker
 *   multiple
 *   accept=".pdf,.docx"
 *   fileTypeHint="*PDF, DOCX (Not more than 10 MB each)"
 *   fileStates={upload.getFileGroup('documents')}
 *   onFilesChange={(files) => upload.addFilesToGroup('documents', files)}
 *   onRemoveFile={(key) => upload.removeFile(key)}
 *   onRetryFile={(key) => upload.retryFile(key)}
 * />
 */

import React, { useRef } from 'react';
import { ImagePreview, ImagePreviewWrapper, UploadWrapper } from '../input-text/input.styled';
import Heading from '../typography';
import ProgressBarCustom from '../progress-bar/progressBar';
import type { FileState, FileStatus } from '../../hooks/custom-hooks/useStreamingUpload';
import {
  ProgressWrapper,
  StatusIcon,
  ActionButtons,
  ActionButton,
  ErrorMessage,
} from './StreamingFilePicker.styled';
import { FILE_SIZE } from '../../constants/common';

/** Default max file size in bytes (from constants/common.ts) */
const DEFAULT_MAX_FILE_SIZE = FILE_SIZE; // 10 MB

export interface StreamingFilePickerProps {
  /** Accepted file types (e.g., ".xlsx,.zip") */
  accept?: string;
  /** Help text showing allowed file types */
  fileTypeHint?: string;
  /** Error message to display */
  error?: string;
  /** Disable the input */
  disabled?: boolean;
  /** HTML id for the input */
  id?: string;
  /** Maximum file size in bytes (default: FILE_SIZE from constants/common.ts = 10 MB) */
  maxFileSize?: number;

  // ── Single-file mode props ──
  /** Current file state from useStreamingUpload (single-file mode) */
  fileState?: FileState;
  /** Called when user selects a file (single-file mode) */
  onFileChange?: (file: File | undefined) => void;
  /** Called when user clicks retry on a failed upload (single-file mode) */
  onRetry?: () => void;

  // ── Multi-file mode props ──
  /** Enable multi-file selection */
  multiple?: boolean;
  /** File states for multi-file mode (from upload.getFileGroup) */
  fileStates?: Array<{ key: string; state: FileState }>;
  /** Called when user selects files in multi-file mode */
  onFilesChange?: (files: File[]) => void;
  /** Called to remove a specific file in multi-file mode */
  onRemoveFile?: (fileKey: string) => void;
  /** Called to retry a specific file in multi-file mode */
  onRetryFile?: (fileKey: string) => void;
}

const getStatusIcon = (status: FileStatus): JSX.Element | null => {
  switch (status) {
    case 'success':
      return <StatusIcon className='material-symbols-outlined success'>check_circle</StatusIcon>;
    case 'failed':
      return null;
    case 'uploading':
    case 'queued':
      return <StatusIcon className='material-symbols-outlined uploading'>sync</StatusIcon>;
    default:
      return null;
  }
};

/** Renders a single file row with progress/status/actions */
const FilePreviewRow: React.FC<{
  state: FileState;
  onRemove: () => void;
  onRetry?: () => void;
}> = ({ state, onRemove, onRetry }) => {
  const isFileUploading = state.status === 'uploading' || state.status === 'queued';

  return (
    <ImagePreview>
      <div className='image-wrapper' title={state.file.name}>
        {isFileUploading ? (
          <ProgressWrapper>
            <ProgressBarCustom
              size={38}
              progress={state.progress}
              trackWidth={3}
              indicatorWidth={3}
              indicatorColor='#6366f1'
              trackColor='#e5e7eb'
            />
            <span className='progress-text'>{Math.round(state.progress)}%</span>
          </ProgressWrapper>
        ) : (
          <span className='material-symbols-outlined image-icon'>draft</span>
        )}
        <span className='image-filename'>{state.file.name}</span>
      </div>

      <ActionButtons>
        {!isFileUploading && getStatusIcon(state.status)}

        {state.status === 'failed' && onRetry && (
          <ActionButton type='button' className='retry-btn' onClick={onRetry} title='Retry upload'>
            <span className='material-symbols-outlined'>refresh</span>
          </ActionButton>
        )}

        {!isFileUploading && (
          <div className='image-cross' onClick={onRemove}>
            <span className='material-symbols-outlined image-delete'>delete</span>
          </div>
        )}
      </ActionButtons>
    </ImagePreview>
  );
};

export const StreamingFilePicker: React.FC<StreamingFilePickerProps> = ({
  accept = '.pdf, .jpg, .jpeg, .png, .docx, .doc',
  fileTypeHint,
  error,
  disabled = false,
  id,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  // Single-file props
  fileState,
  onFileChange,
  onRetry,
  // Multi-file props
  multiple = false,
  fileStates,
  onFilesChange,
  onRemoveFile,
  onRetryFile,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = React.useState<string | undefined>();

  // Convert bytes to MB for display
  const maxSizeMB = maxFileSize / (1024 * 1024);

  const resetInput = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files;
    setValidationError(undefined);

    if (!files || files.length === 0) {
      resetInput();
      return;
    }

    // Check if file size validation is enabled (maxFileSize > 0 and not Infinity)
    const hasFileSizeLimit = maxFileSize > 0 && maxFileSize !== Infinity;

    if (multiple) {
      // Multi-file mode: validate all, then call onFilesChange
      const validFiles: File[] = [];
      for (const file of Array.from(files)) {
        if (hasFileSizeLimit && file.size > maxFileSize) {
          const fileSizeMB = file.size / (1024 * 1024);
          setValidationError(
            `"${file.name}" exceeds ${maxSizeMB} MB limit (${fileSizeMB.toFixed(2)} MB).`
          );
          resetInput();
          return;
        }
        validFiles.push(file);
      }
      onFilesChange?.(validFiles);
    } else {
      // Single-file mode (existing logic)
      const file = files[0];
      if (file) {
        if (hasFileSizeLimit && file.size > maxFileSize) {
          const fileSizeMB = file.size / (1024 * 1024);
          setValidationError(
            `File size exceeds ${maxSizeMB} MB limit. Selected file is ${fileSizeMB.toFixed(2)} MB.`
          );
          resetInput();
          return;
        }
        onFileChange?.(file);
      }
    }

    // Reset input value to allow re-uploading the same file
    resetInput();
  };

  const handleSingleRemove = (): void => {
    setValidationError(undefined);
    onFileChange?.(undefined);
    resetInput();
  };

  // Build unified list of files to display
  const filesToDisplay: Array<{ key: string; state: FileState }> = multiple
    ? fileStates || []
    : fileState
      ? [{ key: id || '_single', state: fileState }]
      : [];

  const hasAnyFile = filesToDisplay.length > 0;
  const hasAnyUploading = filesToDisplay.some(
    (f) => f.state.status === 'uploading' || f.state.status === 'queued'
  );
  const hasAnyFailed = filesToDisplay.some((f) => f.state.status === 'failed');

  const displayError =
    validationError || error || (!multiple ? fileState?.error : undefined);
  const hasError = !!displayError || hasAnyFailed;

  // In single mode: always show input (unless disabled).
  // In multi mode: always show input so user can add more files (unless disabled).
  const showUploadInput = !disabled;

  return (
    <>
      <UploadWrapper id={id} className={hasError ? 'is-invalid' : ''}>
        {/* File preview(s) */}
        <ImagePreviewWrapper>
          {filesToDisplay.map(({ key, state }) => (
            <FilePreviewRow
              key={key}
              state={state}
              onRemove={
                multiple
                  ? () => onRemoveFile?.(key)
                  : handleSingleRemove
              }
              onRetry={
                multiple
                  ? state.status === 'failed'
                    ? () => onRetryFile?.(key)
                    : undefined
                  : onRetry
              }
            />
          ))}
        </ImagePreviewWrapper>

        {/* Upload input area */}
        {showUploadInput && (
          <>
            <div className='uploadDetail'>
              <span className='material-symbols-outlined mr-1'>backup</span>
              <div>
                <Heading variant='body-xs' title='Click to Upload or Drag or Drop' />
                <Heading
                  variant='body-xs'
                  title={fileTypeHint || `*PNG, JPG, JPEG, PDF, DOCX (Not more than 10 MB)`}
                />
              </div>
            </div>

            {/* Native file input */}
            <input
              ref={fileInputRef}
              id={id ? `${id}-input` : undefined}
              type='file'
              accept={accept}
              multiple={multiple}
              onChange={handleFileChange}
            />
          </>
        )}
      </UploadWrapper>

      {/* Error message below the upload area */}
      {displayError && <ErrorMessage>{displayError}</ErrorMessage>}
    </>
  );
};

export default StreamingFilePicker;
